/* ================================================
   MasterValue Tax Exemption Form
   File: assets/tax-exemption-form.js
   ================================================ */

(function () {
  'use strict';

  const app = document.getElementById('te-app');
  if (!app) return;

  const isLoggedIn  = app.dataset.loggedIn === 'true';
  if (!isLoggedIn) return;

  const API_BASE     = (app.dataset.apiBase || '').replace(/\/$/, '');
  const CUSTOMER_ID  = app.dataset.customerId;
  const CUSTOMER_EMAIL = app.dataset.customerEmail;
  const CUSTOMER_NAME  = app.dataset.customerName;

  // ── DOM ───────────────────────────────────────
  const loadingEl    = document.getElementById('te-loading');
  const formEl       = document.getElementById('te-form');
  const alreadyApp   = document.getElementById('te-already-approved');
  const alreadyPend  = document.getElementById('te-already-pending');
  const successEl    = document.getElementById('te-success');
  const submitBtn    = document.getElementById('te-submit');
  const btnText      = document.getElementById('te-btn-text');
  const btnLoading   = document.getElementById('te-btn-loading');
  const formErr      = document.getElementById('te-form-error');
  const formErrMsg   = document.getElementById('te-form-error-msg');

  // ── Step 1: Check existing status on load ─────
  async function checkStatus() {
    try {
      const r = await fetch(`${API_BASE}/api/status?customerId=${encodeURIComponent(CUSTOMER_ID)}`);
      const data = await r.json();

      hide(loadingEl);

      if (data.status === 'approved') {
        show(alreadyApp);
      } else if (data.status === 'pending') {
        show(alreadyPend);
      } else {
        show(formEl);
      }
    } catch (e) {
      // If status check fails, just show the form
      hide(loadingEl);
      show(formEl);
    }
  }

  checkStatus();

  // ── EIN auto-format ───────────────────────────
  const einInput = document.getElementById('te-ein');
  if (einInput) {
    einInput.addEventListener('input', function () {
      let val = this.value.replace(/\D/g, '').substring(0, 9);
      if (val.length > 2) val = val.slice(0, 2) + '-' + val.slice(2);
      this.value = val;
    });
  }

  // ── File upload handling ──────────────────────
  const dropzone   = document.getElementById('te-dropzone');
  const fileInput  = document.getElementById('te-certificate');
  const idleView   = document.getElementById('te-dropzone-idle');
  const selectedView = document.getElementById('te-dropzone-selected');
  const fileNameEl = document.getElementById('te-file-name');
  const removeBtn  = document.getElementById('te-remove-file');

  if (fileInput) {
    fileInput.addEventListener('change', () => handleFileSelected(fileInput.files[0]));
  }

  if (removeBtn) {
    removeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      clearFile();
    });
  }

  // Drag and drop
  if (dropzone) {
    dropzone.addEventListener('dragover', (e) => { e.preventDefault(); dropzone.classList.add('te-drag-over'); });
    dropzone.addEventListener('dragleave', () => dropzone.classList.remove('te-drag-over'));
    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.classList.remove('te-drag-over');
      const file = e.dataTransfer?.files?.[0];
      if (file) {
        // Transfer to input
        const dt = new DataTransfer();
        dt.items.add(file);
        fileInput.files = dt.files;
        handleFileSelected(file);
      }
    });
  }

  function handleFileSelected(file) {
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;

    // Check by extension as fallback — some browsers return empty MIME for PDFs
    const ext = file.name.split('.').pop().toLowerCase();
    const allowedExts = ['jpg', 'jpeg', 'png', 'webp', 'pdf'];
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', ''];

    if (!allowedExts.includes(ext) && !allowedTypes.includes(file.type)) {
      showFieldError('certificate', 'Please upload a JPG, PNG, or PDF file');
      clearFile();
      return;
    }
    if (file.size > maxSize) {
      showFieldError('certificate', 'File is too large. Max size is 5MB');
      clearFile();
      return;
    }

    // Show selected state — file is valid
    fileNameEl.textContent = file.name;
    hide(idleView);
    show(selectedView);
    dropzone.classList.add('te-has-file');
    dropzone.classList.remove('te-error-zone');
    // Clear both the error message AND the red border
    clearFieldError('certificate');
    const errEl = document.getElementById('err-certificate');
    if (errEl) { errEl.textContent = ''; errEl.classList.remove('te-visible'); }
  }

  function clearFile() {
    fileInput.value = '';
    fileNameEl.textContent = '';
    show(idleView);
    hide(selectedView);
    dropzone.classList.remove('te-has-file');
  }

  // ── Validation ────────────────────────────────
  function validateEIN(ein) {
    const clean = ein.replace(/\s/g, '');
    if (!/^\d{2}-\d{7}$/.test(clean)) return false;
    const bad = ['00','07','08','09','17','18','19','28','29','49','69','70','78','79','89','96','97'];
    return !bad.includes(clean.substring(0, 2));
  }

  function validateForm() {
    let valid = true;

    const businessName = document.getElementById('te-business-name');
    const businessType = document.getElementById('te-business-type');
    const state        = document.getElementById('te-state');
    const ein          = document.getElementById('te-ein');
    const certify      = document.getElementById('te-certify');

    // Business name
    if (!businessName.value.trim()) {
      showFieldError('businessName', 'Please enter your legal business name');
      businessName.classList.add('te-input--error');
      valid = false;
    } else {
      clearFieldError('businessName');
      businessName.classList.remove('te-input--error');
    }

    // Business type
    if (!businessType.value) {
      showFieldError('businessType', 'Please select a business type');
      businessType.classList.add('te-input--error');
      valid = false;
    } else {
      clearFieldError('businessType');
      businessType.classList.remove('te-input--error');
    }

    // State
    if (!state.value) {
      showFieldError('state', 'Please select your state of registration');
      state.classList.add('te-input--error');
      valid = false;
    } else {
      clearFieldError('state');
      state.classList.remove('te-input--error');
    }

    // EIN
    if (!validateEIN(ein.value)) {
      showFieldError('ein', 'Please enter a valid EIN in XX-XXXXXXX format (e.g. 12-3456789)');
      ein.classList.add('te-input--error');
      valid = false;
    } else {
      clearFieldError('ein');
      ein.classList.remove('te-input--error');
    }

    // Certificate
    if (!fileInput.files || fileInput.files.length === 0) {
      showFieldError('certificate', 'Please upload your tax exemption certificate — it\'s required for instant approval');
      dropzone.classList.add('te-error-zone');
      valid = false;
    } else {
      clearFieldError('certificate');
      dropzone.classList.remove('te-error-zone');
    }

    // Certification checkbox
    if (!certify.checked) {
      showFieldError('certified', 'You must certify that the information is accurate');
      valid = false;
    } else {
      clearFieldError('certified');
    }

    return valid;
  }

  // ── Form submission ───────────────────────────
  if (formEl) {
    formEl.addEventListener('submit', async function (e) {
      e.preventDefault();
      hide(formErr);

      if (!validateForm()) return;

      // Build FormData (handles file upload)
      const fd = new FormData();
      fd.append('customerId', CUSTOMER_ID);
      fd.append('customerEmail', CUSTOMER_EMAIL);
      fd.append('customerName', CUSTOMER_NAME);
      fd.append('businessName', document.getElementById('te-business-name').value.trim());
      fd.append('businessType', document.getElementById('te-business-type').value);
      fd.append('state', document.getElementById('te-state').value);
      fd.append('ein', document.getElementById('te-ein').value.trim());
      fd.append('certified', 'yes');
      if (fileInput.files[0]) {
        fd.append('certificate', fileInput.files[0]);
      }

      // Loading state
      setLoading(true);

      try {
        const response = await fetch(`${API_BASE}/api/apply`, {
          method: 'POST',
          body: fd,
          // Don't set Content-Type — browser sets it with boundary for multipart
        });

        const data = await response.json();

        if (data.success) {
          // Show success screen with animation
          hide(formEl);
          show(successEl);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (data.alreadyApproved) {
          hide(formEl);
          show(alreadyApp);
        } else if (data.alreadyPending) {
          hide(formEl);
          show(alreadyPend);
        } else if (data.errors) {
          // Field-level errors from server
          Object.entries(data.errors).forEach(([key, msg]) => showFieldError(key, msg));
        } else {
          showFormError(data.error || 'Something went wrong. Please try again or contact info@mastervaluewholesale.com');
        }
      } catch (err) {
        showFormError('Network error — please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    });
  }

  // ── Helpers ───────────────────────────────────
  function show(el) { if (el) el.style.display = ''; }
  function hide(el) { if (el) el.style.display = 'none'; }

  function setLoading(on) {
    submitBtn.disabled = on;
    btnText.style.display    = on ? 'none' : '';
    btnLoading.style.display = on ? '' : 'none';
  }

  function showFieldError(key, msg) {
    const el = document.getElementById(`err-${key}`);
    if (el) { el.textContent = msg; el.classList.add('te-visible'); }
  }

  function clearFieldError(key) {
    const el = document.getElementById(`err-${key}`);
    if (el) { el.textContent = ''; el.classList.remove('te-visible'); }
  }

  function showFormError(msg) {
    formErrMsg.textContent = msg;
    show(formErr);
    formErr.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

})();